import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Badge } from "@app/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@app/components/ui/tabs";
import {
  ExamplesTable,
  type ExampleReview,
} from "@app/components/ExamplesTable";
import { CorrectionsPanel } from "@app/components/CorrectionsPanel";
import { ExampleEditor } from "@app/components/ExampleEditor";
import { ExperimentCard } from "@app/components/ExperimentCard";
import ScopedPathwayBoard from "@app/components/dashboard-pathway/ScopedPathwayBoard";
import {
  Plus,
  Upload,
  GitCompare,
  FlaskConical,
  Search,
  BarChart3,
  Database,
  Layers,
  PencilLine,
  CalendarClock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import DatasetStateBadge from "@app/components/DatasetStateBadge";
import SealedReferenceView from "@app/components/SealedReferenceView";
import API from "@app/services/api";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import {
  readRefusal,
  type ApiRefusal,
  type DatasetState,
} from "@app/utils/reference-dataset";
import { Plural, Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";

interface Dataset {
  id: string;
  name: string;
  description: string;
  file_count: number;
  created_at: string;
  specialite?: string | null;
  state: DatasetState;
  sealed_at: string | null;
}

interface DatasetExample {
  id: string;
  inputs: Record<string, unknown>;
  /** Instantané figé à l'association du patient — conservé comme trace, jamais noté. */
  expected_outputs: Record<string, unknown> | null;
  /** Clé de réponse vive, résolue par le serveur avec la fonction qu'utilise le scorer. */
  answer_key?: {
    values: Record<string, unknown>;
    emptied: string[];
    size: number;
  } | null;
  metadata: Record<string, unknown> | null;
  source_type: string | null;
  created_at: string;
  updated_at: string;
}

interface Experiment {
  id: string;
  name: string;
  description: string | null;
  status: string;
  total_runs: number;
  completed_runs: number;
  failed_runs: number;
  avg_latency_ms: number | null;
  avg_score: number | null;
  created_at: string;
}

export default function DatasetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLingui();
  const labReadOnly = useLabReadOnly();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [examples, setExamples] = useState<DatasetExample[]>([]);
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, _setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingExample, setEditingExample] = useState<DatasetExample | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expSearch, setExpSearch] = useState("");
  const [expStatusFilter, setExpStatusFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("data");
  const [examplesTotal, setExamplesTotal] = useState(0);
  const [episodeSyncing, setEpisodeSyncing] = useState(false);
  const [episodeReport, setEpisodeReport] = useState<{
    created: number;
    reused: number;
    patients_without_pathway: number;
  } | null>(null);
  const [loadingMoreExamples, setLoadingMoreExamples] = useState(false);
  const [datasetPatients, setDatasetPatients] = useState<
    Array<{
      patient_id_magex: string;
      patient_tenant_id: string;
      patient_id_patient: number;
      patient_nom: string | null;
      patient_prenom: string | null;
      /** Every ``id_magex`` naming this SAME dossier, resolved by the server: an association
       *  and an example may name different ids of one patient. */
      patient_id_magex_versions: string[];
    }>
  >([]);
  const [datasetPatientsLoading, setDatasetPatientsLoading] = useState(true);
  /**
   * L'état de revue des dossiers épinglés, lu sur `/correction/stats`.
   *
   * Le même service que l'onglet Corrections, jamais un second calcul : « ce dossier
   * a-t-il été revu » et « combien de dossiers ont été revus » doivent être deux
   * lectures du même nombre, sinon l'en-tête et l'onglet se contrediront.
   */
  const [reviewStats, setReviewStats] = useState<{
    patients_reviewed: number;
    patients_in_scope: number | null;
    median_fields_corrected: number | null;
    by_patient: Array<{
      tenant_id: string;
      id_patient: number;
      fields_reviewed: number;
      fields_validated: number;
      fields_corrected: number;
      fields_flagged: number;
    }>;
  } | null>(null);

  const [sealOpen, setSealOpen] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [sealRefusal, setSealRefusal] = useState<ApiRefusal | null>(null);

  const loadAll = async () => {
    const loaded = await loadDataset();
    // A sealed reference has its own page, which asks only for what the API lists of it.
    if (!loaded || loaded.state === "sealed") return;
    loadExamples();
    loadExperiments();
    loadDatasetPatients();
    loadReviewStats();
  };

  useEffect(() => {
    if (id) void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when dataset id changes
  }, [id]);

  const handleSeal = async () => {
    setSealing(true);
    setSealRefusal(null);
    try {
      const response = await API.post({ path: `/dataset/${id}/seal` });
      if (response?.ok) {
        setSealOpen(false);
        await loadAll();
        return;
      }
      setSealRefusal(
        readRefusal(response) ?? {
          message: t`Le jeu n'a pas pu être scellé.`,
          conflicts: [],
        },
      );
    } finally {
      setSealing(false);
    }
  };

  const loadReviewStats = async () => {
    if (!id) return;
    try {
      const res = await API.get({
        path: "/correction/stats",
        query: { dataset_id: id },
      });
      if (res.ok && res.data) {
        setReviewStats({
          patients_reviewed: res.data.totals.patients_reviewed,
          patients_in_scope: res.data.totals.patients_in_scope,
          median_fields_corrected: res.data.totals.median_fields_corrected,
          by_patient: res.data.by_patient,
        });
      }
    } catch (err) {
      // L'en-tête sait se taire : l'absence de compteur vaut mieux qu'un compteur faux.
      console.error("Erreur lors du chargement de l'état de revue", err);
    }
  };

  const loadDatasetPatients = async () => {
    if (!id) return;
    setDatasetPatientsLoading(true);
    try {
      const res = await API.get({ path: `/dataset/${id}/patients` });
      if (res.ok && res.data?.associations) {
        setDatasetPatients(
          (
            res.data.associations as Array<{
              patient_id_magex?: string;
              patient_tenant_id?: string;
              patient_id_patient?: number;
              patient_nom?: string | null;
              patient_prenom?: string | null;
              patient_id_magex_versions?: string[];
            }>
          )
            .filter((a) => a.patient_id_magex != null)
            .map((a) => ({
              patient_id_magex: a.patient_id_magex!,
              patient_tenant_id: a.patient_tenant_id ?? "",
              patient_id_patient: a.patient_id_patient ?? 0,
              patient_nom: a.patient_nom ?? null,
              patient_prenom: a.patient_prenom ?? null,
              patient_id_magex_versions: a.patient_id_magex_versions ?? [
                a.patient_id_magex!,
              ],
            })),
        );
      } else {
        setDatasetPatients([]);
      }
    } catch {
      setDatasetPatients([]);
    } finally {
      setDatasetPatientsLoading(false);
    }
  };

  const loadDataset = async (): Promise<Dataset | null> => {
    try {
      const response = await API.get({ path: `/dataset/${id}` });
      if (response.ok) {
        setDataset(response.data);
        return response.data as Dataset;
      } else {
        setError(
          response.error || t`Erreur lors du chargement du jeu de données`,
        );
      }
    } catch (e) {
      setError(t`Erreur lors du chargement du jeu de données`);
      console.error("Error loading dataset:", e);
    }
    return null;
  };

  const EXAMPLES_PAGE_SIZE = 200;

  const loadExamples = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${id}/examples`,
        query: { limit: EXAMPLES_PAGE_SIZE, offset: 0 },
      });
      if (response.ok) {
        setExamples(response.data.examples || []);
        setExamplesTotal(response.data.pagination?.total || 0);
      }
    } catch (e) {
      console.error("Error loading examples:", e);
    }
  };

  const loadMoreExamples = async () => {
    setLoadingMoreExamples(true);
    try {
      const response = await API.get({
        path: `/dataset/${id}/examples`,
        query: { limit: EXAMPLES_PAGE_SIZE, offset: examples.length },
      });
      if (response.ok) {
        setExamples((prev) => [...prev, ...(response.data.examples || [])]);
        setExamplesTotal(response.data.pagination?.total || 0);
      }
    } catch (e) {
      console.error("Error loading more examples:", e);
    } finally {
      setLoadingMoreExamples(false);
    }
  };

  const loadExperiments = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${id}/experiments`,
        query: { limit: 100 },
      });
      if (response.ok) {
        setExperiments(response.data.experiments || []);
      }
    } catch (e) {
      console.error("Error loading experiments:", e);
    }
  };

  // Examples handlers
  const handleCreateExample = async (data: {
    inputs: Record<string, unknown>;
    expected_outputs: Record<string, unknown> | null;
  }) => {
    try {
      const response = await API.post({
        path: `/dataset/${id}/examples`,
        body: data,
      });
      if (response.ok) {
        loadExamples();
      } else {
        // A reference and a work dataset never share a dossier: the refusal says which one.
        toast.error(
          readRefusal(response)?.message ?? t`L'exemple n'a pas pu être ajouté.`,
        );
      }
    } catch (e) {
      console.error("Error creating example:", e);
    }
  };

  const handleUpdateExample = async (data: {
    inputs: Record<string, unknown>;
    expected_outputs: Record<string, unknown> | null;
  }) => {
    if (!editingExample) return;
    try {
      const response = await API.put({
        path: `/dataset/${id}/examples/${editingExample.id}`,
        body: data,
      });
      if (response.ok) {
        loadExamples();
        setEditingExample(null);
      }
    } catch (e) {
      console.error("Error updating example:", e);
    }
  };

  const handleDeleteExample = async (exampleId: string) => {
    if (!confirm(t`Êtes-vous sûr de vouloir supprimer cet exemple ?`)) return;
    try {
      const response = await API.delete({
        path: `/dataset/${id}/examples/${exampleId}`,
      });
      if (response.ok) {
        loadExamples();
      }
    } catch (e) {
      console.error("Error deleting example:", e);
    }
  };

  /** Give every record one row per EPISODE — that is all "scoring by act" is.
   *
   *  Idempotent and additive: a row already sitting on an episode's day is annotated with the act,
   *  never duplicated, and nothing is deleted. A record whose pathway was not built produces nothing
   *  and the report says so — "this dataset cannot be scored by act yet" is an answer one has to be
   *  able to read. */
  const handleGenerateEpisodeExamples = async () => {
    setEpisodeSyncing(true);
    try {
      const response = await API.post({
        path: `/dataset/${id}/examples/from-episodes`,
        body: {},
      });
      if (response.ok) {
        const report = response.data as {
          created: number;
          reused: number;
          patients_without_pathway: number;
        };
        setEpisodeReport(report);
        loadExamples();
      } else {
        console.error("Episode example generation failed", response);
      }
    } catch (e) {
      console.error("Error generating episode examples:", e);
    } finally {
      setEpisodeSyncing(false);
    }
  };

  const handleImportExamples = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        API.getUrl(`/dataset/${id}/examples/import`),
        {
          method: "POST",
          body: formData,
          credentials: "include",
        },
      );
      const data = await response.json();
      if (data.ok) {
        loadExamples();
      }
    } catch (e) {
      console.error("Error importing examples:", e);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Filter experiments
  const filteredExperiments = useMemo(() => {
    let result = experiments;

    if (expSearch.trim()) {
      const q = expSearch.toLowerCase();
      result = result.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          (e.description && e.description.toLowerCase().includes(q)),
      );
    }

    if (expStatusFilter !== "all") {
      result = result.filter((e) => e.status === expStatusFilter);
    }

    return result;
  }, [experiments, expSearch, expStatusFilter]);

  // Unique experiment statuses for filter chips
  const expStatuses = useMemo(() => {
    const statuses = new Set(experiments.map((e) => e.status));
    return Array.from(statuses);
  }, [experiments]);

  /**
   * Map exampleId → { url, name } pour la fiche patient.
   * Résolution par patient_id_magex ou (tenant_id, id_patient).
   * Le nom (nom + prénom) provient des associations dataset/patients afin que la
   * colonne Patient s'affiche même quand le store global des patients ne contient
   * pas ce patient (ex. patient appartenant à un autre médecin).
   */
  const patientResolutionByExampleId = useMemo(() => {
    const allowed = datasetPatients;
    type Info = {
      url: string;
      nom: string | null;
      prenom: string | null;
    };
    const byMagex = new Map<string, Info>();
    const byTenantIdPatient = new Map<string, Info>();
    for (const p of allowed) {
      if (!p.patient_tenant_id || p.patient_id_patient == null) continue;
      const info: Info = {
        url: `/patient/${p.patient_tenant_id}/${p.patient_id_patient}`,
        nom: p.patient_nom,
        prenom: p.patient_prenom,
      };
      // Every id of the dossier leads to the same information; without it the resolution fell
      // back on (tenant, id_patient), which most production examples do not carry.
      for (const version of p.patient_id_magex_versions) {
        byMagex.set(version, info);
      }
      byTenantIdPatient.set(
        `${p.patient_tenant_id}\t${p.patient_id_patient}`,
        info,
      );
    }
    const links = new Map<string, string>();
    const names = new Map<
      string,
      { nom: string | null; prenom: string | null }
    >();
    for (const ex of examples) {
      const idMagex = (ex.metadata?.patient_id_magex ??
        ex.inputs?.patient_id_magex) as string | undefined;
      const tenantId = (ex.metadata?.tenant_id ?? ex.inputs?.tenant_id) as
        | string
        | undefined;
      const idPatientRaw = ex.metadata?.id_patient ?? ex.inputs?.id_patient;
      const idPatient = idPatientRaw != null ? Number(idPatientRaw) : undefined;
      let info: Info | undefined;
      if (idMagex && byMagex.has(idMagex)) {
        info = byMagex.get(idMagex);
      } else if (
        tenantId != null &&
        idPatient != null &&
        !Number.isNaN(idPatient)
      ) {
        info = byTenantIdPatient.get(`${tenantId}\t${idPatient}`);
      }
      if (info) {
        links.set(ex.id, info.url);
        if (info.nom != null || info.prenom != null) {
          names.set(ex.id, { nom: info.nom, prenom: info.prenom });
        }
      }
    }
    return { links, names };
  }, [datasetPatients, examples]);
  const patientLinkByExampleId = patientResolutionByExampleId.links;
  const patientNameByExampleId = patientResolutionByExampleId.names;

  /**
   * Map exemple id → état de revue, résolue par la même clé métier que le lien patient.
   *
   * Passer par l'URL déjà calculée plutôt que refaire la résolution : les deux colonnes
   * doivent désigner le même dossier, et deux résolutions parallèles finiraient par
   * diverger sur le cas qui compte (plusieurs versions d'un même patient).
   */
  const reviewByExampleId = useMemo(() => {
    const byKey = new Map<string, ExampleReview>();
    for (const row of reviewStats?.by_patient ?? []) {
      // Same URL as the link above: the two columns join on it.
      byKey.set(`/patient/${row.tenant_id}/${row.id_patient}`, {
        reviewed: row.fields_reviewed,
        validated: row.fields_validated,
        corrected: row.fields_corrected,
        flagged: row.fields_flagged,
      });
    }
    const out = new Map<string, ExampleReview>();
    for (const [exampleId, url] of patientLinkByExampleId) {
      const review = byKey.get(url);
      if (review) out.set(exampleId, review);
    }
    return out;
  }, [reviewStats, patientLinkByExampleId]);

  if (loading && !dataset) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-slate-500">
            <Trans>Chargement...</Trans>
          </p>
        </div>
      </div>
    );
  }

  if (error && !dataset) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!dataset) return null;

  if (dataset.state === "sealed") {
    return <SealedReferenceView dataset={dataset} onChanged={() => void loadAll()} />;
  }

  const constituting = dataset.state === "constituting";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link to="/datasets" className="text-sm text-primary hover:underline">
          <Trans>← Retour aux jeux de données</Trans>
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold">{dataset.name}</h1>
            <DatasetStateBadge state={dataset.state} />
          </div>
          {constituting && !labReadOnly && (
            <Button
              onClick={() => {
                setSealRefusal(null);
                setSealOpen(true);
              }}
            >
              <Trans>Sceller…</Trans>
            </Button>
          )}
        </div>
        <p className="text-slate-600 whitespace-pre-line">
          {dataset.description}
        </p>
        <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
          <span>
            <Plural
              value={examplesTotal || examples.length}
              one="# exemple"
              other="# exemples"
            />
          </span>
          <span>
            <Plural
              value={experiments.length}
              one="# expérience"
              other="# expériences"
            />
          </span>
          {/* La revue, à côté du reste et non dans un onglet : « ce jeu de données
              a-t-il été annoté » est la première question qu'on se pose en l'ouvrant,
              et jusqu'ici il fallait la deviner. La médiane accompagne le compte parce
              qu'un dossier ouvert et un dossier travaillé ne sont pas la même chose. */}
          {reviewStats && reviewStats.patients_in_scope !== null && (
            <span className="font-medium text-slate-700">
              <Trans>
                {reviewStats.patients_reviewed}/{reviewStats.patients_in_scope}{" "}
                dossiers revus
              </Trans>
              {reviewStats.median_fields_corrected !== null && (
                <span className="ml-1 font-normal text-slate-500">
                  <Plural
                    value={reviewStats.median_fields_corrected}
                    one="· médiane # champ corrigé"
                    other="· médiane # champs corrigés"
                  />
                </span>
              )}
            </span>
          )}
        </div>
        {constituting && (
          <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm">
            <p className="font-semibold text-blue-900">
              <Trans>Jeu de référence en constitution.</Trans>
            </p>
            <p className="mt-1 text-slate-700">
              <Trans>
                On y ajoute des dossiers et on les annote ; aucune expérience n'y est
                lancée avant le scellé, et ses corrections ne servent pas à régler la
                lecture. Une fois scellé, sa vérité est figée et il ne montre plus que
                des scores agrégés.
              </Trans>
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab);
        }}
      >
        {/* Autant de colonnes que d'onglets. La grille était restée à cinq après le retrait
            de l'onglet « Entraînement » : la barre gardait une cinquième colonne vide à droite
            de « Corrections », qui se lisait comme un onglet cassé plutôt que comme un onglet
            retiré. */}
        <TabsList
          className={`grid ${constituting ? "grid-cols-3" : "grid-cols-4"} w-full max-w-3xl`}
        >
          <TabsTrigger value="data" className="flex items-center gap-1">
            <Database className="h-4 w-4" />
            <Trans>Données</Trans>
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            <Trans>Statistiques</Trans>
          </TabsTrigger>
          {/* No experiment runs on a reference before its seal: the tab would offer a refusal. */}
          {!constituting && (
            <TabsTrigger
              data-tour="dataset-experiment"
              value="experiments"
              className="flex items-center gap-1"
            >
              <FlaskConical className="h-4 w-4" />
              <Trans>Expériences ({experiments.length})</Trans>
            </TabsTrigger>
          )}
          <TabsTrigger value="corrections" className="flex items-center gap-1">
            <PencilLine className="h-4 w-4" />
            <Trans>Corrections</Trans>
          </TabsTrigger>
        </TabsList>

        {/* ═══════ DONNÉES TAB (Exemples) ═══════ */}
        <TabsContent value="data" className="mt-4">
          <div className="space-y-6">
            {/* Section Exemples */}
            <Card data-tour="dataset-examples">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-5 w-5 text-green-600" />
                    <CardTitle>
                      <Trans>Exemples ({examples.length})</Trans>
                    </CardTitle>
                  </div>
                  {!labReadOnly && (
                    <div className="flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.json,.jsonl"
                        onChange={handleImportExamples}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        <Trans>Importer CSV/JSON</Trans>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateEpisodeExamples}
                        disabled={episodeSyncing}
                      >
                        <CalendarClock className="h-4 w-4 mr-1" />
                        <Trans>Une ligne par épisode</Trans>
                      </Button>
                      <Button size="sm" onClick={() => setEditorOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        <Trans>Ajouter un exemple</Trans>
                      </Button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  <Trans>
                    Paires entrée/sortie utilisées pour l'entraînement et
                    l'évaluation
                  </Trans>
                </p>
                {/* What the generation did, including what it could not do: a record with no built
                    pathway produces no row, and saying nothing would suggest the dataset is scored
                    by act when it is not. */}
                {episodeReport && (
                  <p className="mt-2 text-xs text-slate-600">
                    <Trans>
                      {episodeReport.created} lignes créées,{" "}
                      {episodeReport.reused} réutilisées.
                    </Trans>
                    {episodeReport.patients_without_pathway > 0 && (
                      <span className="ml-1 text-slate-500">
                        <Trans>
                          {episodeReport.patients_without_pathway} dossiers sans
                          parcours construit — non notables par acte.
                        </Trans>
                      </span>
                    )}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <ExamplesTable
                  examples={examples}
                  onDelete={labReadOnly ? undefined : handleDeleteExample}
                  onEdit={
                    labReadOnly
                      ? undefined
                      : (example) => {
                          setEditingExample(example);
                        }
                  }
                  total={examplesTotal}
                  onLoadMore={loadMoreExamples}
                  loadingMore={loadingMoreExamples}
                  patientLinkByExampleId={patientLinkByExampleId}
                  patientNameByExampleId={patientNameByExampleId}
                  patientLinksLoading={datasetPatientsLoading}
                  reviewByExampleId={reviewByExampleId}
                />
              </CardContent>
            </Card>
          </div>

          {/* Create example dialog */}
          <ExampleEditor
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            onSave={handleCreateExample}
          />

          {/* Edit example dialog */}
          {editingExample && (
            <ExampleEditor
              open={true}
              onClose={() => setEditingExample(null)}
              onSave={handleUpdateExample}
              initialInputs={editingExample.inputs}
              initialExpectedOutputs={editingExample.expected_outputs}
              title={t`Modifier l'exemple`}
              hideInputs={true}
            />
          )}
        </TabsContent>

        {/* ═══════ STATISTIQUES TAB ═══════ */}
        <TabsContent value="stats" className="mt-4">
          {id && <ScopedPathwayBoard key={id} datasetId={id} />}
        </TabsContent>

        {/* ═══════ CORRECTIONS TAB ═══════ */}
        <TabsContent value="corrections" className="mt-4">
          {/* Scoped to the patients this dataset pins — which is a slice and says so.
              Most corrections in production are written on records that belong to no
              dataset, so the corpus-wide reading lives in Laboratoire → Corrections. */}
          <CorrectionsPanel datasetId={id} embedded />
        </TabsContent>

        {/* ═══════ EXPERIENCES TAB ═══════ */}
        <TabsContent value="experiments" className="mt-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-700">
              <Trans>Expériences</Trans>
            </h3>
            <div className="flex gap-2 flex-wrap items-center">
              {experiments.length >= 2 && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/datasets/${id}/experiments/compare`)
                  }
                >
                  <GitCompare className="h-4 w-4 mr-1" />
                  <Trans>Comparer</Trans>
                </Button>
              )}
              {!labReadOnly && (
                <Button
                  onClick={() => navigate(`/datasets/${id}/experiments/new`)}
                >
                  <FlaskConical className="h-4 w-4 mr-1" />
                  <Trans>Nouvelle expérience</Trans>
                </Button>
              )}
            </div>
          </div>

          {/* Search & filter bar for experiments */}
          {experiments.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t`Rechercher une expérience...`}
                  value={expSearch}
                  onChange={(e) => setExpSearch(e.target.value)}
                  className="pl-9 h-9 border-slate-200 text-sm"
                />
              </div>
              <div className="flex items-center gap-1">
                <Badge
                  variant={expStatusFilter === "all" ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setExpStatusFilter("all")}
                >
                  <Trans>Tous ({experiments.length})</Trans>
                </Badge>
                {expStatuses.map((st) => (
                  <Badge
                    key={st}
                    variant={expStatusFilter === st ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() =>
                      setExpStatusFilter(expStatusFilter === st ? "all" : st)
                    }
                  >
                    {st} ({experiments.filter((e) => e.status === st).length})
                  </Badge>
                ))}
              </div>
              {(expSearch || expStatusFilter !== "all") && (
                <span className="text-xs text-slate-400">
                  {filteredExperiments.length} / {experiments.length}
                </span>
              )}
            </div>
          )}

          {experiments.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FlaskConical className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">
                  <Trans>Aucune expérience.</Trans>
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  <Trans>
                    Créez une expérience pour tester vos modèles sur les
                    exemples de ce jeu de données.
                  </Trans>
                </p>
                {!labReadOnly && (
                  <Button
                    className="mt-4"
                    onClick={() => navigate(`/datasets/${id}/experiments/new`)}
                  >
                    <FlaskConical className="h-4 w-4 mr-1" />
                    <Trans>Créer une expérience</Trans>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : filteredExperiments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>
                <Trans>
                  Aucune expérience ne correspond à votre recherche.
                </Trans>
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setExpSearch("");
                  setExpStatusFilter("all");
                }}
              >
                <Trans>Réinitialiser les filtres</Trans>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredExperiments.map((exp) => (
                <ExperimentCard
                  key={exp.id}
                  experiment={exp}
                  datasetId={id!}
                  onDelete={
                    labReadOnly
                      ? undefined
                      : async (experimentId) => {
                          await API.delete({
                            path: `/dataset/${id}/experiments/${experimentId}`,
                          });
                          setExperiments((prev) =>
                            prev.filter((e) => e.id !== experimentId),
                          );
                        }
                  }
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={sealOpen} onOpenChange={setSealOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              <Trans>Sceller « {dataset.name} » ?</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                Sa vérité est figée à cet instant : les corrections faites ensuite
                servent au soin, pas au score. Aucun dossier n'y entre plus, et il ne
                montre plus que des scores agrégés. Seul un administrateur peut le
                desceller, définitivement.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          {sealRefusal && (
            <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
              <p className="font-semibold text-red-800">{sealRefusal.message}</p>
              {sealRefusal.conflicts.length > 0 && (
                <ul className="max-h-48 space-y-1 overflow-y-auto text-slate-700">
                  {sealRefusal.conflicts.map((conflict) => (
                    <li key={`${conflict.tenant_id}/${conflict.id_patient}`}>
                      <Link
                        to={`/patient/${conflict.tenant_id}/${conflict.id_patient}`}
                        className="font-medium text-primary hover:underline"
                      >
                        <Trans>Dossier n° {conflict.id_patient}</Trans>
                      </Link>{" "}
                      ·{" "}
                      {conflict.datasets
                        .map((holder) => holder.name ?? holder.id)
                        .join(", ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSealOpen(false)}>
              <Trans>Annuler</Trans>
            </Button>
            <Button disabled={sealing} onClick={() => void handleSeal()}>
              {sealing ? t`Scellement en cours...` : t`Sceller`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
